// Utilitários de formatação para a aplicação UtilyHub

// Formatar valores monetários
export const money = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined || isNaN(Number(value))) {
    return 'R$ 0,00';
  }
  
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numValue);
};

// Formatar CNPJ com máscara — uso pra DISPLAY de valor completo (14 dígitos).
// Pra input controlado use `maskCnpjInput` que aceita string parcial sem
// padStart e cresce conforme o usuário digita.
export const formatCnpj = (cnpj?: string | number): string => {
  if (!cnpj) return '';
  const cnpjStr = String(cnpj).replace(/\D/g, '').padStart(14, '0');
  return cnpjStr.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
};

// Máscara progressiva pra input controlado — aplica "XX.XXX.XXX/XXXX-XX"
// conforme o usuário digita. Strip não-dígitos e limita a 14. Aceita valor
// já formatado de volta (idempotente).
export const maskCnpjInput = (value: string): string => {
  const d = value.replace(/\D/g, '').slice(0, 14);
  if (d.length === 0) return '';
  if (d.length <= 2) return d;
  if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
  if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
  if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
};

// Máscara progressiva pra telefone brasileiro — fixo (10 díg.) ou celular (11).
// Suporta digitação parcial mostrando "(XX) XXXX(X)-XXXX" conforme cresce.
// Idempotente: passar valor já formatado retorna o mesmo formato.
export const maskTelefoneInput = (value: string): string => {
  const d = value.replace(/\D/g, '').slice(0, 11);
  if (d.length === 0) return '';
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  // 7-10 dígitos: fixo (XX) XXXX-XXXX (a vírgula muda no 11º)
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  // 11 dígitos: celular (XX) XXXXX-XXXX
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

// Strip de máscara — retorna só dígitos. Use antes de mandar pra API:
// máscara é responsabilidade do frontend, backend grava cru pra normalizar
// dados entre clientes e simplificar validação.
export const unmaskDigits = (value?: string | null): string =>
  (value ?? '').replace(/\D/g, '');

// Formatar tipo de frete com descrição
export const formatTipoFrete = (tipo?: string): { label: string; color: string } => {
  if (!tipo) return { label: 'Não informado', color: 'text-zinc-500' };
  
  switch (tipo.toUpperCase()) {
    case 'CIF':
      return { label: 'CIF (Remetente)', color: 'text-blue-600' };
    case 'FOB':
      return { label: 'FOB (Destinatário)', color: 'text-emerald-600' };
    default:
      return { label: tipo, color: 'text-zinc-700' };
  }
};

// Formatar data ISO para formato brasileiro (apenas data)
export const formatDate = (isoDate: string): string => {
  if (!isoDate) return '';

  try {
    const date = new Date(isoDate);
    return date.toLocaleDateString('pt-BR');
  } catch {
    return isoDate;
  }
};

// Formatar data ISO para formato brasileiro com hora (dd/mm/aaaa hh:mm)
export const formatDateTime = (dateString: string | undefined | null, fallback = '-'): string => {
  if (!dateString) return fallback;
  try {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateString;
  }
};

// Normalizar texto para busca (remove acentos, espaços extras, etc)
export const normalizeText = (text: string | null | undefined): string => {
  if (!text) return '';
  
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove acentos
    .trim();
};

